/*
GPS DataViz

Copyright © 2021 GPSDataViz

Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
*/

import React from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
	Layout,
	Menu,
	Popover,
	Button,
	Popconfirm,
	Space,
	Skeleton,
	Image,
	Tag,
} from "antd";
import { Link } from "react-router-dom";
import { TfiLock } from "react-icons/tfi";
import { LuChevronDown } from "react-icons/lu";


import "../../Styles/styles-Header.css";
import { COMPANY_LOGO } from "../../Components/Report/constants";
import "./Navbar.css";
import { useAuth0 } from "@auth0/auth0-react";
import { useSelector } from "react-redux";
import { logoutRedirectUrl } from "../../auth/auth0";
import { TeamSelectionMenu } from "./TeamSelectionMenu";
import { NavbarInbox } from "@components/Common/Inbox";

const { Header } = Layout;

export const Navbar = () => {
	const { logout } = useAuth0();
	const location = useLocation();
	const navigate = useNavigate();
	const {
		info: { team_logo, dvai_enabled, scatterplot_enabled },
	} = useSelector(({ team }) => team);

	const {
		currentUser: { email },
	} = useSelector(({ auth }) => auth);

	const handleLogout = () => {
		logout({ logoutParams: { returnTo: logoutRedirectUrl } });
		localStorage.removeItem("team-id");
	};

	const currentPath = location?.pathname?.replace('/', '');
	// Every Settings tab (settings/<tab>) selects the one Settings item.
	const selectedKey = currentPath.startsWith('settings/') ? 'settings' : currentPath;
	const parentLabel = (label, paths) => (
		<span className="navbar-parent-label">
			{paths.includes(currentPath) ? <b>{label}</b> : label}
			<LuChevronDown className="navbar-parent-chevron" />
		</span>
	);
	const popupItem = (path, title, subtitle) => ({
		key: path.slice(1),
		label: (
			<NavLink to={path}>
				<div>{title}</div>
				<div className="navbar-item-subtitle">{subtitle}</div>
			</NavLink>
		),
	});

	const menuItems = [
		{
			key: 'overview-menu',
			label: parentLabel("Overview", ["overview", "status-tracker-v2"]),
			onTitleClick: () => navigate('/overview'),
			popupClassName: 'navbar-popup',
			children: [
				popupItem('/overview', 'Overview', "Your team's training calendar at a glance"),
				popupItem('/status-tracker-v2', 'Status Tracker', 'Player availability and flags by day'),
			],
		},
		{ key: 'upload-data', label: <NavLink to="/upload-data">Upload</NavLink> },
		{ key: 'reports', label: <NavLink to="/reports">Reports</NavLink> },
		{ key: 'sandbox', label: <NavLink to="/sandbox">Sandbox</NavLink> },
		{ key: 'teamdata', label: <NavLink to="/teamdata">Team</NavLink> },
		{ key: 'playerdata', label: <NavLink to="/playerdata">Players</NavLink> },
		{
			key: 'analyze-menu',
			label: parentLabel("Analyze", ["breakdown", "baseline", "drills", "weekly-buckets", "leaderboard", "scatterplot", "compare"]),
			onTitleClick: () => navigate('/breakdown'),
			popupClassName: 'navbar-popup',
			children: [
				popupItem('/breakdown', 'Daily Breakdown', 'One day, every player, every metric'),
				popupItem('/baseline', 'Baselines', 'Each player against their baseline'),
				popupItem('/drills', 'Drills', 'Drill loads by position'),
				popupItem('/weekly-buckets', 'Weekly Buckets', 'Weekly load and ACWR per player'),
				popupItem('/leaderboard', 'Leaderboard', 'Rank players by any metric'),
				{ ...popupItem('/scatterplot', 'Scatterplot', 'Plot players on two metrics'), icon: scatterplot_enabled ? null : <TfiLock/> },
				popupItem('/compare', 'Compare', 'Days, players and date ranges side by side'),
			],
		},
		{ key: 'chatbot', icon: dvai_enabled ? null : <TfiLock/>, label: <NavLink to="/chatbot">Whistle AI</NavLink> },
		{
			key: 'health-menu',
			label: parentLabel("Health", ["health/treatment-log", "health/injuries", "health/return-to-play"]),
			onTitleClick: () => navigate('/health/treatment-log'),
			popupClassName: 'navbar-popup',
			children: [
				popupItem('/health/treatment-log', 'Treatment Log', 'Treatments by body area and timing'),
				popupItem('/health/injuries', 'Injuries', 'Log injuries and follow each recovery'),
				popupItem('/health/return-to-play', 'Return to Play', 'Phased plans back from injury'),
			],
		},
		{ key: 'settings', label: <NavLink to="/settings/overview">Settings</NavLink> },
	];

	return (
		<>
			<div className="outer-navbar">
				<div className="navbar-navbar">
					<Layout className="layout" style={{width: '100%'}}>
						<Header
							style={{
								backgroundColor: "white",
								display: "flex",
								padding: 0,
								alignItems: "center",
								borderBottom: "1px solid rgba(5, 5, 5, 0.06)",
								// justifyContent: "flex-start",
								// width: "100%",
							}}
						>

							<div className="navbar-side navbar-side-left">
								<NavLink to="/overview">
									<img className="navbar-logo" src={COMPANY_LOGO} alt="Whistle Performance"/>
								</NavLink>
							</div>

							<Menu
								theme="light"
								mode="horizontal"
								defaultSelectedKeys={[selectedKey]}
								selectedKeys={[selectedKey]}
								multiple={false}
								className="gpsdv-navbar"
								items={menuItems}
							/>

							<div className="navbar-side navbar-side-right">
								<NavbarInbox />
								<Popover
									placement="bottomRight"
									title={
										<div style={{ padding: "2px 0" }}>
											<div style={{ fontSize: 11, fontWeight: 400, color: "rgba(0, 0, 0, 0.45)", lineHeight: 1.4 }}>
												Signed in as
											</div>
											<div
												style={{
													fontSize: 13,
													fontWeight: 600,
													color: "rgba(0, 0, 0, 0.88)",
													overflow: "hidden",
													textOverflow: "ellipsis",
													whiteSpace: "nowrap",
												}}
												title={email}
											>
												{email}
											</div>
										</div>
									}
									content={
										<div style={{ width: 360 }}>
											<TeamSelectionMenu />
											<div style={{ textAlign: "center", marginTop: 12 }}>
												<Space>
													<Button type="primary">
														<Link to="/help-center">
															Help Center
														</Link>
													</Button>
													<Popconfirm
														placement="bottomRight"
														title="Are you sure?"
														onConfirm={handleLogout}
														okText="Yes"
														cancelText="No"
													>
														<Button type="primary">
															Logout
														</Button>
													</Popconfirm>
												</Space>
											</div>
										</div>
									}
								>
									<div style={{ cursor: "pointer", display: "inline-flex" }}>
										{team_logo ? (
											<Image
												id="team-logo"
												src={team_logo}
												width={60}
												preview={false}
											/>
										) : (
											<Skeleton.Avatar
												active={true}
												size={"large"}
												style={{
													width: 60,
													height: 60,
												}}
											/>
										)}
									</div>
								</Popover>
							</div>
						</Header>
					</Layout>
				</div>
			</div>
		</>
	);
};
